import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogIn, LogOut, Plus, Edit2, Trash2, Search, Package,
  Truck, MapPin, CheckCircle2, X, RefreshCw, AlertCircle,
  ArrowLeft, ShieldCheck
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "skt_admin_token";
const TOKEN_EXPIRY_KEY = "skt_admin_token_expiry";
const ADMIN_EMAIL_KEY = "skt_admin_email";

const stageConfig = {
  material_received: { label: "Material Received", icon: Package },
  material_despatched: { label: "Material Despatched", icon: Truck },
  out_for_delivery: { label: "Out for Delivery", icon: MapPin },
  delivered: { label: "Delivered", icon: CheckCircle2 }
};

const stageOrder = ["material_received", "material_despatched", "out_for_delivery", "delivered"];

// Helper: make authenticated API call
const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isSessionValid = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!token || !expiry) return false;
  return new Date().getTime() < parseInt(expiry, 10);
};

const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(ADMIN_EMAIL_KEY);
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminPanel = ({ onClose }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [createForm, setCreateForm] = useState({
    sender_name: "", sender_address: "",
    receiver_name: "", receiver_address: "", receiver_phone: "",
    origin: "", destination: "", weight: "", description: ""
  });

  const handleLogout = useCallback(() => {
    clearSession();
    setIsLoggedIn(false);
    setConsignments([]);
    setAdminEmail("");
  }, []);

  const fetchConsignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/consignments`, { headers: authHeaders() });
      setConsignments(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setLoginError("Your session has expired. Please log in again.");
      } else {
        console.error("Failed to fetch consignments:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  // Check existing session on mount
  useEffect(() => {
    if (isSessionValid()) {
      setIsLoggedIn(true);
      setAdminEmail(localStorage.getItem(ADMIN_EMAIL_KEY) || "");
      fetchConsignments();
    } else {
      clearSession();
    }
  }, [fetchConsignments]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        email: loginForm.email.trim(),
        password: loginForm.password,
      });
      if (res.data?.success && res.data?.token) {
        const expiryMs = new Date().getTime() + (res.data.expires_in_hours || 24) * 60 * 60 * 1000;
        localStorage.setItem(TOKEN_KEY, res.data.token);
        localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryMs));
        localStorage.setItem(ADMIN_EMAIL_KEY, res.data.email || loginForm.email);
        setAdminEmail(res.data.email || loginForm.email);
        setIsLoggedIn(true);
        setLoginForm({ email: "", password: "" });
        fetchConsignments();
      } else {
        setLoginError(res.data?.message || "Login failed");
      }
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConsignment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/consignments`, createForm, { headers: authHeaders() });
      setShowCreateModal(false);
      setCreateForm({
        sender_name: "", sender_address: "",
        receiver_name: "", receiver_address: "", receiver_phone: "",
        origin: "", destination: "", weight: "", description: ""
      });
      fetchConsignments();
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      else alert(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create consignment");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStage = async (trackingNumber, newStage) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/consignments/${trackingNumber}/stage`,
        { stage: newStage, description: "" },
        { headers: authHeaders() }
      );
      setShowStageModal(false);
      setSelectedConsignment(null);
      fetchConsignments();
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      else alert(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update stage");
    }
  };

  const handleDeleteConsignment = async (trackingNumber) => {
    if (!window.confirm(`Delete consignment ${trackingNumber}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/consignments/${trackingNumber}`, { headers: authHeaders() });
      fetchConsignments();
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      else alert(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const filteredConsignments = consignments.filter(c =>
    c.tracking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.sender_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.receiver_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // ============ Login Screen ============
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B192C] via-[#102540] to-[#0B192C] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8"
          data-testid="admin-login-card"
        >
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 bg-[#E63946] rounded-2xl flex items-center justify-center mb-4">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#0B192C]">SKT Admin Portal</h2>
            <p className="text-sm text-slate-500 mt-1">Sign in to manage consignments</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0B192C] mb-2">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#E63946] focus:outline-none"
                placeholder="contact@sktlogistics.co.in"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B192C] mb-2">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#E63946] focus:outline-none"
                placeholder="Enter password"
                data-testid="admin-password-input"
              />
            </div>

            {loginError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm" data-testid="admin-login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#E63946] text-white font-semibold rounded-xl hover:bg-[#c92a37] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 text-sm text-slate-500 hover:text-[#0B192C]"
              data-testid="admin-back-to-site"
            >
              ← Back to main site
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ============ Dashboard ============
  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#0B192C] text-white py-4 px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">SKT Logistics — Admin Dashboard</h1>
              <p className="text-xs text-white/60">Signed in as {adminEmail}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Actions Bar */}
        <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#E63946] text-white rounded-lg hover:bg-[#c92a37] transition-colors"
              data-testid="create-consignment-btn"
            >
              <Plus className="w-4 h-4" />
              New Consignment
            </button>
            <button
              onClick={fetchConsignments}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="refresh-consignments-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search tracking #, sender, receiver..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-[#E63946] w-72"
              data-testid="search-consignments-input"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total", count: consignments.length, color: "bg-blue-500" },
            { label: "In Transit", count: consignments.filter(c => c.current_stage === 'material_despatched').length, color: "bg-orange-500" },
            { label: "Out for Delivery", count: consignments.filter(c => c.current_stage === 'out_for_delivery').length, color: "bg-yellow-500" },
            { label: "Delivered", count: consignments.filter(c => c.current_stage === 'delivered').length, color: "bg-green-500" },
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <p className="text-sm text-slate-500">{stat.label}</p>
              <p className="text-2xl font-bold text-[#0B192C]">{stat.count}</p>
              <div className={`w-full h-1 ${stat.color} rounded-full mt-2 opacity-30`} />
            </div>
          ))}
        </div>

        {/* Consignments Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="consignments-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Tracking #</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Sender</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Receiver</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Route</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Created</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredConsignments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      {loading ? "Loading..." : "No consignments found"}
                    </td>
                  </tr>
                ) : (
                  filteredConsignments.map((consignment) => (
                    <tr key={consignment.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-[#E63946]">
                          {consignment.tracking_number}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#0B192C]">{consignment.sender_name}</p>
                        <p className="text-xs text-slate-500">{consignment.origin}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#0B192C]">{consignment.receiver_name}</p>
                        <p className="text-xs text-slate-500">{consignment.destination}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {consignment.origin} → {consignment.destination}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          consignment.current_stage === 'delivered'
                            ? 'bg-green-100 text-green-700'
                            : consignment.current_stage === 'out_for_delivery'
                            ? 'bg-yellow-100 text-yellow-700'
                            : consignment.current_stage === 'material_despatched'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {stageConfig[consignment.current_stage]?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatDate(consignment.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedConsignment(consignment);
                              setShowStageModal(true);
                            }}
                            className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg"
                            title="Update Status"
                            data-testid={`update-stage-${consignment.tracking_number}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteConsignment(consignment.tracking_number)}
                            className="p-2 hover:bg-red-50 text-red-600 rounded-lg"
                            title="Delete"
                            data-testid={`delete-${consignment.tracking_number}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
            >
              <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-xl font-bold text-[#0B192C]">Create New Consignment</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateConsignment} className="p-6 space-y-4" data-testid="create-consignment-form">
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { k: "sender_name", label: "Sender Name *", req: true },
                    { k: "sender_address", label: "Sender Address *", req: true },
                    { k: "receiver_name", label: "Receiver Name *", req: true },
                    { k: "receiver_address", label: "Receiver Address *", req: true },
                    { k: "receiver_phone", label: "Receiver Phone", req: false },
                    { k: "weight", label: "Weight (kg)", req: false },
                    { k: "origin", label: "Origin *", req: true, ph: "e.g., Bengaluru" },
                    { k: "destination", label: "Destination *", req: true, ph: "e.g., Chennai" },
                  ].map(f => (
                    <div key={f.k}>
                      <label className="block text-sm font-medium text-[#0B192C] mb-1">{f.label}</label>
                      <input
                        type="text"
                        required={f.req}
                        value={createForm[f.k]}
                        onChange={(e) => setCreateForm({ ...createForm, [f.k]: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#E63946] focus:outline-none"
                        placeholder={f.ph || ""}
                        data-testid={`create-${f.k}`}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B192C] mb-1">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#E63946] focus:outline-none resize-none"
                    rows={3}
                    placeholder="Package description..."
                    data-testid="create-description"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="px-4 py-2 bg-[#E63946] text-white rounded-lg hover:bg-[#c92a37] disabled:opacity-50" data-testid="submit-create-consignment">
                    {loading ? "Creating..." : "Create Consignment"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Stage Update Modal */}
      <AnimatePresence>
        {showStageModal && selectedConsignment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-[#0B192C]">Update Status</h2>
                  <p className="text-sm text-slate-500 font-mono">{selectedConsignment.tracking_number}</p>
                </div>
                <button onClick={() => { setShowStageModal(false); setSelectedConsignment(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-3">
                <p className="text-sm text-slate-600 mb-4">Select the current stage:</p>
                {stageOrder.map((stageKey) => {
                  const config = stageConfig[stageKey];
                  const Icon = config.icon;
                  const isCurrentStage = selectedConsignment.current_stage === stageKey;
                  const currentStageIndex = stageOrder.indexOf(selectedConsignment.current_stage);
                  const thisStageIndex = stageOrder.indexOf(stageKey);
                  const isCompleted = thisStageIndex <= currentStageIndex;

                  return (
                    <button
                      key={stageKey}
                      onClick={() => handleUpdateStage(selectedConsignment.tracking_number, stageKey)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        isCurrentStage
                          ? 'border-[#E63946] bg-red-50'
                          : isCompleted
                          ? 'border-green-200 bg-green-50'
                          : 'border-slate-200 hover:border-[#E63946] hover:bg-slate-50'
                      }`}
                      data-testid={`stage-btn-${stageKey}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCurrentStage ? 'bg-[#E63946] text-white' : isCompleted ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="text-left flex-1">
                        <p className={`font-semibold ${isCurrentStage ? 'text-[#E63946]' : 'text-[#0B192C]'}`}>
                          {config.label}
                        </p>
                        {isCurrentStage && <p className="text-xs text-[#E63946]">Current Status</p>}
                      </div>
                      {isCompleted && !isCurrentStage && (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
